import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Plus, Mail } from 'lucide-react';
import { getUserFoodEntries, getNotifications, getUnreadNotificationCount, markNotificationRead, markAllNotificationsRead, getPopupNotifications, dismissPopupNotification } from '@/utils/api';
import { toast } from 'sonner';
import FoodUploadModal from '@/components/FoodUploadModal';
import TextEntryModal from '@/components/TextEntryModal';
import FoodGallery from '@/components/FoodGallery';
import BiometricsPage from '@/pages/BiometricsPage';
import MyFoodSummary from '@/components/MyFoodSummary';
import FeedbackTab from '@/components/FeedbackTab';
import OnboardingModal from '@/components/OnboardingModal';
import DashboardTutorialModal from '@/components/DashboardTutorialModal';

// Extracted dashboard components
import {
  DashboardNavbar,
  DashboardMenu,
  FloatingActionButtons,
  PopupNotificationModal,
} from '@/components/dashboard';

/**
 * UserDashboard - Main user dashboard page
 * Refactored to use extracted sub-components for navbar, menu, FABs, and popups
 */
const UserDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, token } = useAuth();
  
  // Core state
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState('food-diary');
  
  // Modal state
  const [showUpload, setShowUpload] = useState(false);
  const [showTextEntry, setShowTextEntry] = useState(false);
  const [uploadType, setUploadType] = useState('meal');
  const [textEntryType, setTextEntryType] = useState('meal');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDashboardTutorial, setShowDashboardTutorial] = useState(false);
  
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [popupNotification, setPopupNotification] = useState(null);
  
  // Menu state
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [hasMyInsulAccess, setHasMyInsulAccess] = useState(false);
  
  // Refs
  const menuRef = useRef(null);
  const userMenuRef = useRef(null);

  // Check if we should show onboarding
  useEffect(() => {
    if (location.state?.showOnboarding) {
      setShowOnboarding(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Check if we should show dashboard tutorial
  useEffect(() => {
    const checkDashboardTutorial = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/dashboard-tutorial-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (!data.has_seen_dashboard_tutorial) {
          setShowDashboardTutorial(true);
        }
      } catch (error) {
        console.error('Error checking tutorial status:', error);
      }
    };
    
    if (token && !showOnboarding) {
      const timer = setTimeout(checkDashboardTutorial, 500);
      return () => clearTimeout(timer);
    }
  }, [token, showOnboarding]);

  // Fetch popup notifications
  useEffect(() => {
    const fetchPopupNotifications = async () => {
      try {
        const popups = await getPopupNotifications(token);
        if (popups && popups.length > 0) {
          setPopupNotification(popups[0]);
        }
      } catch (error) {
        console.error('Error fetching popup notifications:', error);
      }
    };
    
    if (token) {
      fetchPopupNotifications();
    }
  }, [token]);

  // Check MyInsul access
  useEffect(() => {
    const checkMyInsulAccess = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/myinsul/access-check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        setHasMyInsulAccess(data.has_access || false);
      } catch (error) {
        setHasMyInsulAccess(false);
      }
    };
    
    if (token) {
      checkMyInsulAccess();
    }
  }, [token]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Data fetching callbacks
  const fetchEntries = useCallback(async () => {
    try {
      const data = await getUserFoodEntries(token);
      setEntries(data);
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
      toast.error('Failed to load food entries');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifs, countData] = await Promise.all([
        getNotifications(token),
        getUnreadNotificationCount(token)
      ]);
      setNotifications(notifs);
      setUnreadCount(countData.count);
    } catch (error) {
      console.error('Failed to load notifications');
    }
  }, [token]);

  // Main data fetch
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchEntries();
    fetchNotifications();
  }, [user, navigate, fetchEntries, fetchNotifications]);

  // === Event Handlers ===

  const handleMarkRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId, token);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? {...n, is_read: true} : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead(token);
      setNotifications(notifications.map(n => ({...n, is_read: true})));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const handleDismissPopup = async () => {
    if (popupNotification) {
      try {
        await dismissPopupNotification(popupNotification.id, token);
      } catch (error) {
        console.error('Failed to dismiss popup');
      }
      setPopupNotification(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openUpload = (type) => {
    setUploadType(type);
    setShowUpload(true);
  };

  const openTextEntry = (type) => {
    setTextEntryType(type);
    setShowTextEntry(true);
  };

  const handleUploadSuccess = () => {
    setShowUpload(false);
    fetchEntries();
  };

  const handleTextEntrySuccess = () => {
    setShowTextEntry(false);
    fetchEntries();
  };

  // Guard clause
  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      {/* Navbar */}
      <DashboardNavbar
        user={user}
        notifications={notifications}
        unreadCount={unreadCount}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        userMenuOpen={userMenuOpen}
        setUserMenuOpen={setUserMenuOpen}
        userMenuRef={userMenuRef}
        hasMyInsulAccess={hasMyInsulAccess}
        onMarkRead={handleMarkRead}
        onMarkAllRead={handleMarkAllRead}
        onLogout={handleLogout}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 flex-1">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Header with Menu */}
          <div className="flex items-center justify-between mb-6">
            <DashboardMenu
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
              menuRef={menuRef}
            />
            <div className="text-right">
              <p className="text-sm text-muted-foreground hidden sm:block" data-testid="dashboard-user-greeting">{user.first_name ? `Hi, ${user.first_name}` : user.email}</p>
            </div>
          </div>

          {/* Content Area */}
          <div className="w-full">
            {activeTab === 'food-diary' && (
              <div className="space-y-8">
                {loading ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">Loading your meals...</p>
                  </div>
                ) : loadError ? (
                  <div className="text-center py-16" data-testid="food-entries-load-error">
                    <p className="text-muted-foreground mb-6">We couldn't load your food diary. Please check your connection and try again.</p>
                    <Button
                      data-testid="retry-load-entries-button"
                      variant="outline"
                      className="rounded-full px-8"
                      onClick={() => { setLoading(true); fetchEntries(); }}
                    >
                      Retry
                    </Button>
                  </div>
                ) : entries.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-16"
                  >
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-2xl font-bold uppercase tracking-wide text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>IN</span>
                    </div>
                    <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Start Your Journey
                    </h2>
                    <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                      Track your meals, snacks, and drinks visually
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                      <Button
                        data-testid="add-first-meal-button"
                        className="rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95 bg-gradient-to-r from-primary to-[#8FD14F]"
                        onClick={() => openUpload('meal')}
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Meal
                      </Button>
                      <Button
                        data-testid="add-first-snack-button"
                        className="rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95 bg-gradient-to-r from-secondary to-[#FF9B50]"
                        onClick={() => openUpload('snack')}
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Snack
                      </Button>
                      <Button
                        data-testid="add-first-drink-button"
                        variant="outline"
                        className="rounded-full px-8 py-6 text-lg font-bold border-2 hover:bg-primary/10"
                        onClick={() => openUpload('drink')}
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Drink
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground mt-6">
                      No photo? <button 
                        onClick={() => openTextEntry('meal')} 
                        className="text-amber-600 font-medium hover:underline"
                        data-testid="describe-meal-link"
                      >
                        Describe your meal instead
                      </button>
                    </p>
                  </motion.div>
                ) : (
                  <div>
                    <div className="mb-6 text-center">
                      <p className="text-base text-muted-foreground">
                        {entries.length} {entries.length === 1 ? 'item' : 'items'} tracked
                      </p>
                    </div>
                    <FoodGallery entries={entries} onEntryUpdate={fetchEntries} token={token} />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'food-summary' && <MyFoodSummary entries={entries} />}
            {activeTab === 'biometrics' && <BiometricsPage />}
            {activeTab === 'feedback' && <FeedbackTab token={token} />}
          </div>
        </motion.div>
      </main>

      {/* Floating Action Buttons */}
      {activeTab === 'food-diary' && entries.length > 0 && (
        <FloatingActionButtons
          onAddMeal={() => openUpload('meal')}
          onAddSnack={() => openUpload('snack')}
          onAddDrink={() => openUpload('drink')}
          onTextEntry={() => openTextEntry('meal')}
        />
      )}

      {/* Modals */}
      {showUpload && (
        <FoodUploadModal
          onClose={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
          mealType={uploadType}
        />
      )}

      {showTextEntry && (
        <TextEntryModal
          onClose={() => setShowTextEntry(false)}
          onSuccess={handleTextEntrySuccess}
          entryType={textEntryType}
        />
      )}

      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        token={token}
      />

      <DashboardTutorialModal
        isOpen={showDashboardTutorial}
        onClose={() => setShowDashboardTutorial(false)}
        token={token}
      />

      <PopupNotificationModal
        notification={popupNotification}
        onDismiss={handleDismissPopup}
      />

      {/* Footer */}
      <footer className="w-full py-4 px-6 border-t border-gray-200 bg-white/50 backdrop-blur-sm mt-auto">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            © 2026 Insul Nutrients. All rights reserved.
          </p>
          <Link 
            to="/contact-us" 
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 font-medium"
            data-testid="contact-us-link"
          >
            <Mail className="w-4 h-4" />
            Contact Us
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default UserDashboard;
